import type { MockSocket } from "./mockSocket";
import { FakeWorkstation } from "./fakeWorkstation";
import { getRole, sandboxUser, SandboxUser, SANDBOX_ORG_ID } from "./session";

// The sandbox's stand-in for the backend and the fleet of EEG systems.
// It keeps the same state the server keeps (systems, users, logs, settings),
// answers the same socket events with the same payload shapes, and runs a
// small world clock so things happen on their own: pings move, checks come
// due, other technologists work, systems drop and return.
//
// Wire-format rules copied from the real backend, because the UI depends on
// them: every system field is a string, ids are strings, last_checked is
// epoch milliseconds, "Offline" has a capital O, charge_status is "true"
// or "".

type Sys = Record<string, any>;

interface CheckLog {
  id: number;
  timestamp: string;
  user_id: string;
  username: string;
  patientname: string;
  systemname: string;
  note: string;
}

interface LiveSession {
  id: number;
  user_id: string;
  username: string;
  system_id: string;
  systemname: string;
  patientname: string;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
}

interface Stream {
  pc: RTCPeerConnection;
  station: FakeWorkstation;
  pending: RTCIceCandidateInit[];
  answered: boolean;
  statsTimer: ReturnType<typeof setInterval>;
  session: LiveSession;
}

const MIN = 60_000;
const BUSY = ["RUN_CHECK", "running_bluetooth", "taking_ss", "awaiting_approval", "writing_note"];

const OTHERS: SandboxUser[] = [
  { sub: "u-maria", username: "maria@sandbox.example", fullname: "Maria Alvarez", phonenumber: "+15555550101", organizationid: SANDBOX_ORG_ID, role: "user", created_at: "2025-11-03T15:00:00Z", disabled_at: null },
  { sub: "u-james", username: "james@sandbox.example", fullname: "James Okafor", phonenumber: "+15555550102", organizationid: SANDBOX_ORG_ID, role: "user", created_at: "2026-01-12T15:00:00Z", disabled_at: null },
  { sub: "u-priya", username: "priya@sandbox.example", fullname: "Priya Nair", phonenumber: "+15555550103", organizationid: SANDBOX_ORG_ID, role: "admin", created_at: "2025-09-20T15:00:00Z", disabled_at: null },
  { sub: "u-tom", username: "tom@sandbox.example", fullname: "Tom Becker", phonenumber: "+15555550104", organizationid: SANDBOX_ORG_ID, role: "user", created_at: "2026-03-02T15:00:00Z", disabled_at: null },
];
// Tom is on the roster but not signed in.
const ONLINE_OTHERS = ["u-maria", "u-james", "u-priya"];

class Simulator {
  private client: MockSocket | null = null;
  private timers = new Set<ReturnType<typeof setTimeout>>();
  private loop: ReturnType<typeof setInterval> | null = null;
  private tick = 0;
  private nextLogId = 1;
  private nextSessionId = 1;
  private nextSystemNum = 13;
  private streams = new Map<string, Stream>();
  private pendingCheckShot = new Map<string, string>();

  me: SandboxUser = sandboxUser("user");
  users: SandboxUser[] = [];
  systems = new Map<string, Sys>();
  guids = new Map<string, string>();
  logs: CheckLog[] = [];
  liveSessions: LiveSession[] = [];
  logins: { user_id: string; username: string; logged_in_at: string }[] = [];
  settings = {
    organization_id: SANDBOX_ORG_ID,
    check_reminder_hours: 0,
    check_reminder_minutes: 45,
    send_missed_checks_to_admins: true,
    send_text_reminders: false,
    max_systems_per_user: 12,
    updated_at: new Date().toISOString(),
  };

  // Built lazily: this module is imported by the real app too, and must do
  // nothing there. Data is seeded the first time the sandbox asks for it.
  private seeded = false;

  // ── Lifecycle ────────────────────────────────────────────────────────────

  attach(client: MockSocket) {
    this.client = client;
    this.ensureRole();
    if (!this.loop) this.startWorld();
  }

  /** Reseed if the visitor entered with a different role than the data was built for. */
  ensureRole() {
    const role = getRole() || "user";
    if (!this.seeded || this.me.role !== role) this.seed();
  }

  detach(client: MockSocket) {
    if (this.client !== client) return;
    this.client = null;
    this.streams.forEach((_, id) => this.stopStream(id));
  }

  /** Throw everything away and start from the seed again. */
  reset() {
    this.timers.forEach((t) => clearTimeout(t));
    this.timers.clear();
    if (this.loop) clearInterval(this.loop);
    this.loop = null;
    this.streams.forEach((_, id) => this.stopStream(id));
    this.seed();
    this.startWorld();
    this.send("onlineUsers", this.onlineUsers());
    this.send("systems", this.allSystems());
  }

  private send(event: string, payload?: any) {
    this.client?.deliver(event, payload);
  }

  private after(ms: number, fn: () => void) {
    const t = setTimeout(() => {
      this.timers.delete(t);
      fn();
    }, ms);
    this.timers.add(t);
  }

  // ── Seed data ────────────────────────────────────────────────────────────

  private seed() {
    this.seeded = true;
    const role = getRole() || "user";
    this.me = sandboxUser(role);
    this.users = [this.me, ...OTHERS.map((u) => ({ ...u }))];
    this.systems.clear();
    this.guids.clear();
    this.logs = [];
    this.liveSessions = [];
    this.logins = [];
    this.nextLogId = 1;
    this.nextSessionId = 1;
    this.nextSystemNum = 13;
    this.tick = 0;

    const now = Date.now();
    const me = this.me.sub;
    const row = (n: number, o: Partial<Sys>) => {
      const id = String(900 + n);
      const patient = o.patient_name ?? "";
      const status = o.status ?? (patient ? "online" : "neuroworksClosed");
      this.systems.set(id, {
        id,
        organization_id: String(SANDBOX_ORG_ID),
        system_name: `SYS ${n}`,
        patient_name: patient,
        status,
        study_active: patient && status !== "Offline" ? "true" : "false",
        battery_lvl: "80",
        charge_status: "",
        network_type: "wifi",
        ping: "45",
        claimedby: "",
        last_checked: "",
        last_seen_at: String(now),
        note: "",
        is_live_view: "false",
        remote_session: "",
        version: "3.0.5",
        ...o,
      });
      this.guids.set(id, this.makeGuid());
    };
    const ago = (m: number) => String(now - m * MIN);

    row(1, { patient_name: "Demo Patient A", claimedby: me, last_checked: ago(12), battery_lvl: "86", ping: "42", note: "Checked by " + this.me.fullname });
    row(2, { patient_name: "Demo Patient B", claimedby: me, last_checked: ago(58), battery_lvl: "71", ping: "58", note: "Electrodes intact, patient resting" });
    row(3, { patient_name: "Demo Patient C", claimedby: me, last_checked: ago(31), battery_lvl: "100", charge_status: "true", network_type: "ethernet", ping: "18", note: "Checked by " + this.me.fullname });
    row(4, { claimedby: me, battery_lvl: "64", ping: "66" });
    row(5, { patient_name: "Demo Patient D", claimedby: "u-maria", last_checked: ago(20), battery_lvl: "92", ping: "51", note: "Checked by Maria Alvarez" });
    row(6, { patient_name: "Demo Patient E", status: "Offline", claimedby: "u-maria", last_checked: ago(95), last_seen_at: ago(6), battery_lvl: "23", ping: "0" });
    row(7, { patient_name: "Demo Patient F", claimedby: "u-james", last_checked: ago(40), battery_lvl: "55", ping: "74", remote_session: "teamviewer", note: "Checked by James Okafor" });
    row(8, { patient_name: "Demo Patient G", battery_lvl: "77", ping: "39" });
    row(9, { patient_name: "Demo Patient H", battery_lvl: "48", network_type: "cellular", ping: "380" });
    row(10, { battery_lvl: "100", charge_status: "true", ping: "33" });
    row(11, { patient_name: "Demo Patient J", status: "invalidHeadbox", battery_lvl: "69", ping: "61" });
    row(12, { status: "Offline", last_seen_at: ago(42), battery_lvl: "12", ping: "0" });

    // A day of history so the logs and audit pages have something to show.
    const techs: Record<string, SandboxUser> = {};
    this.users.forEach((u) => (techs[u.sub] = u));
    this.systems.forEach((s) => {
      if (!s.patient_name || !s.claimedby) return;
      const who = techs[s.claimedby];
      const last = Number(s.last_checked) || now;
      // Walk backwards from the latest check in 42 to 54 minute steps, so
      // the history reads as a lab that checks on schedule.
      let ts = last;
      for (let i = 0; i < 15; i++) {
        this.logs.push({
          id: this.nextLogId++,
          timestamp: new Date(ts).toISOString(),
          user_id: who.sub,
          username: who.fullname,
          patientname: s.patient_name,
          systemname: s.system_name,
          note: i % 4 === 3 ? "Electrodes intact, patient resting" : `Checked by ${who.fullname}`,
        });
        ts -= (42 + ((i * 7) % 13)) * MIN;
        // One deliberate long gap for James so the audit page shows what a
        // missed check looks like.
        if (who.sub === "u-james" && i === 6) ts -= 50 * MIN;
      }
      const started = last - 3 * 60 * MIN;
      this.liveSessions.push({
        id: this.nextSessionId++,
        user_id: who.sub,
        username: who.fullname,
        system_id: s.id,
        systemname: s.system_name,
        patientname: s.patient_name,
        started_at: new Date(started).toISOString(),
        ended_at: new Date(started + 7 * MIN).toISOString(),
        duration_seconds: 7 * 60,
      });
    });
    this.logs.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    this.users.forEach((u, i) => {
      if (u.sub === "u-tom") return;
      [26, 2].forEach((h) =>
        this.logins.push({ user_id: u.sub, username: u.fullname, logged_in_at: new Date(now - (h * 60 + i * 7) * MIN).toISOString() }),
      );
    });
    this.logins.push({ user_id: this.me.sub, username: this.me.fullname, logged_in_at: new Date().toISOString() });
  }

  private makeGuid() {
    const hex = () => Math.floor(Math.random() * 0xffff).toString(16).padStart(4, "0");
    return `${hex()}${hex()}-${hex()}-${hex()}-${hex()}-${hex()}${hex()}${hex()}`;
  }

  // ── World clock ──────────────────────────────────────────────────────────

  private startWorld() {
    this.loop = setInterval(() => this.worldTick(), 3000);

    // Two scripted moments so a first-time visitor sees the alerts.
    this.after(35_000, () => {
      const s = this.systems.get("902");
      if (s && s.claimedby === this.me.sub && s.status === "online" && Date.now() - Number(s.last_checked) > 45 * MIN) {
        this.send("check_reminder", { systemId: s.id, systemName: s.system_name, patientName: s.patient_name, reminderTime: "0h 45m" });
      }
    });
    this.after(75_000, () => {
      const s = this.systems.get("906");
      if (this.me.role === "admin" && s && s.claimedby === "u-maria") {
        this.send("admin_alert", { systemId: s.id, systemName: s.system_name, claimedBy: "Maria Alvarez", missedCheckTime: "2h 0m" });
      }
    });
  }

  private worldTick() {
    this.tick++;
    // Ping jitter, sent only when it changes, as the server does.
    this.systems.forEach((s) => {
      if (s.status === "Offline") return;
      const base = Number(s.ping) || 45;
      const next = Math.max(8, Math.round(base + (Math.random() - 0.5) * (base > 200 ? 60 : 14)));
      if (next !== base) {
        s.ping = String(next);
        this.send("ping_update", { id: Number(s.id), ping: next });
      }
    });

    if (this.tick % 7 === 0) this.drainBattery();
    if (this.tick % 14 === 0) this.colleagueChecks();
    if (this.tick % 25 === 0) this.flipFreeSystem();
  }

  private drainBattery() {
    const pool = Array.from(this.systems.values()).filter((s) => s.status !== "Offline");
    const s = pool[Math.floor(Math.random() * pool.length)];
    if (!s) return;
    const lvl = Number(s.battery_lvl);
    s.battery_lvl = String(s.charge_status ? Math.min(100, lvl + 1) : Math.max(5, lvl - 1));
    this.change(s);
  }

  /** Another technologist checks one of their systems. */
  private colleagueChecks() {
    const pool = Array.from(this.systems.values()).filter(
      (s) => s.claimedby && s.claimedby !== this.me.sub && s.status === "online" && s.patient_name && !s.remote_session,
    );
    const s = pool[Math.floor(Math.random() * pool.length)];
    if (!s) return;
    const who = this.users.find((u) => u.sub === s.claimedby)!;
    this.recordCheck(s, who, `Checked by ${who.fullname}`);
    this.change(s);
  }

  /** An unclaimed system drops off the network or comes back. */
  private flipFreeSystem() {
    const pool = Array.from(this.systems.values()).filter((s) => !s.claimedby && !BUSY.includes(s.status));
    const s = pool[Math.floor(Math.random() * pool.length)];
    if (!s) return;
    if (s.status === "Offline") this.bringOnline(s);
    else this.takeOffline(s);
  }

  // ── Helpers shared by socket and HTTP paths ──────────────────────────────

  allSystems() {
    return Array.from(this.systems.values()).map((s) => ({ ...s }));
  }

  onlineUsers() {
    return this.users
      .filter((u) => !u.disabled_at && (u.sub === this.me.sub || ONLINE_OTHERS.includes(u.sub)))
      .map((u) => ({ id: u.sub, name: u.fullname, role: u.role }));
  }

  private isAdmin() {
    return this.me.role === "admin" || this.me.role === "superadmin";
  }

  private change(s: Sys, extra: Sys = {}) {
    this.send("system_change", { ...s, ...extra });
  }

  private fail(systemId: string, reason: string) {
    this.send("system_action_failed", { systemId, reason });
  }

  private recordCheck(s: Sys, who: SandboxUser, note: string, at = Date.now()) {
    s.last_checked = String(at);
    s.note = note;
    this.logs.push({
      id: this.nextLogId++,
      timestamp: new Date(at).toISOString(),
      user_id: who.sub,
      username: who.fullname,
      patientname: s.patient_name,
      systemname: s.system_name,
      note,
    });
  }

  takeOffline(s: Sys) {
    this.stopStream(s.id);
    s.status = "Offline";
    s.study_active = "false";
    s.is_live_view = "false";
    s.last_seen_at = String(Date.now());
    this.send("system_offline", { ...s });
  }

  bringOnline(s: Sys) {
    s.status = s.patient_name ? "online" : "neuroworksClosed";
    s.study_active = s.patient_name ? "true" : "false";
    s.last_seen_at = String(Date.now());
    if (!Number(s.ping)) s.ping = "48";
    this.send("system_online", { ...s });
  }

  // ── Socket events from the app ───────────────────────────────────────────

  handle(event: string, p: any) {
    switch (event) {
      case "login":
        this.send("onlineUsers", this.onlineUsers());
        this.send("systems", this.allSystems());
        return;
      case "getOnlineUsers":
        return this.send("onlineUsers", this.onlineUsers());
      case "claimSystem":
        return this.claim(String(p));
      case "unclaimSystem":
        return this.unclaim(String(p));
      case "assignSystem":
        return this.assign(p);
      case "RUN_CHECK":
        return this.runCheck(String(p?.systemId));
      case "APPROVE_CHECK":
        return this.approveCheck(String(p?.id), p?.note);
      case "DENY_CHECK":
        return this.denyCheck(String(p?.id));
      case "REMOVE_FROM_POOL":
        return this.removeFromPool(String(p?.systemId));
      case "RESTART_PROCESS":
        return this.restartProcess(String(p?.systemId));
      case "admin_broadcast":
        if (!this.isAdmin() || !p?.message) return;
        return this.send("org_toast", { message: String(p.message).slice(0, 500), fromName: this.me.fullname, sentAt: Date.now() });
      case "REQUEST_STREAM":
        return void this.requestStream(String(p?.systemId));
      case "STREAM_ANSWER":
        return void this.streamAnswer(String(p?.systemId), p?.answer);
      case "STREAM_ICE_FROM_FRONTEND":
        return void this.streamIce(String(p?.systemId), p?.candidate);
      case "STOP_STREAM":
        return this.stopStream(String(p?.systemId));
      case "MOUSE_MOVE":
      case "MOUSE_DOWN":
      case "MOUSE_UP":
      case "MOUSE_WHEEL":
      case "KEY_EVENT":
        this.streams.get(String(p?.systemId))?.station.handleInput({ type: event, ...p });
        return;
      case "wn":
        return this.writeNote(String(p?.systemId), p?.note);
      default:
        return; // CLIPBOARD_FROM_USER, SEND_SAS: nothing to simulate
    }
  }

  private claim(id: string) {
    const s = this.systems.get(id);
    if (!s) return this.send("claim_failed", { reason: "System not found. It may have been removed." });
    if (s.claimedby === this.me.sub) return this.send("system_change", { ...s });
    if (s.claimedby) return this.send("claim_failed", { reason: "This system was just claimed by another user." });
    if (!this.isAdmin()) {
      const cap = this.settings.max_systems_per_user;
      const mine = Array.from(this.systems.values()).filter((x) => x.claimedby === this.me.sub).length;
      if (mine >= cap) {
        return this.send("claim_failed", { reason: `You can only claim up to ${cap} systems. Unclaim one first.` });
      }
    }
    s.claimedby = this.me.sub;
    this.change(s);
  }

  private unclaim(id: string) {
    const s = this.systems.get(id);
    if (!s) return;
    if (s.claimedby !== this.me.sub && !this.isAdmin()) {
      return this.fail(id, "You can only unclaim systems you claimed.");
    }
    this.stopStream(id);
    s.claimedby = null;
    this.change(s);
  }

  private assign(p: { systemId: string; userId: string }) {
    if (!this.isAdmin()) return this.send("claim_failed", { reason: "Admin permission required." });
    if (!p?.systemId || !p?.userId) return this.send("claim_failed", { reason: "Missing system or target user." });
    const s = this.systems.get(String(p.systemId));
    if (!s) return this.send("claim_failed", { reason: "System not found." });
    if (!this.users.some((u) => u.sub === p.userId)) {
      return this.send("claim_failed", { reason: "Target user is not in your organization." });
    }
    s.claimedby = p.userId;
    this.change(s);
  }

  // A system check, step for step as the workstation performs it, with the
  // waits shortened so a visitor is not left watching a spinner.
  private runCheck(id: string) {
    const s = this.systems.get(id);
    if (!s) return;
    if (s.claimedby !== this.me.sub) {
      // The server ignores this silently; resync so the card does not hang.
      return this.send("system_change", { ...s });
    }
    if (s.study_active !== "true") {
      this.send("claim_failed", { reason: "Cannot run check — no active study on this system." });
      return this.send("system_change", { ...s });
    }
    if (s.status === "Offline") {
      this.send("system_offline", { ...s });
      return this.fail(id, "System is not reachable right now — it may still be reconnecting. Try again shortly.");
    }
    const alive = () => this.systems.get(id) === s && s.status !== "Offline";

    s.status = "running_bluetooth";
    this.change(s);
    this.after(1800, () => {
      if (!alive()) return;
      s.status = "taking_ss";
      this.change(s);
      [10, 35, 60, 85, 100].forEach((pct, i) =>
        this.after(250 + i * 280, () => alive() && this.send("ss_progress", { systemId: Number(id), pct })),
      );
      this.after(1900, () => {
        if (!alive()) return;
        const shot = new FakeWorkstation(s.patient_name, s.system_name).still();
        this.pendingCheckShot.set(id, shot);
        s.status = "awaiting_approval";
        this.change(s);
        this.change(s, { screenshot: shot });
      });
    });
  }

  private approveCheck(id: string, note?: string) {
    const s = this.systems.get(id);
    if (!s) return;
    if (s.claimedby !== this.me.sub) {
      this.fail(id, "This system is no longer claimed by you. Reclaim it to approve the check.");
      return this.send("system_change", { ...s });
    }
    const finalNote = (typeof note === "string" && note.trim()) || `Checked by ${this.me.fullname}`;
    this.pendingCheckShot.delete(id);
    s.status = "writing_note";
    s.note = finalNote;
    this.change(s);
    this.after(1500, () => {
      if (this.systems.get(id) !== s || s.status !== "writing_note") return;
      s.status = "online";
      this.recordCheck(s, this.me, finalNote);
      this.streams.get(id)?.station.addNote(finalNote);
      this.change(s);
    });
  }

  private denyCheck(id: string) {
    const s = this.systems.get(id);
    if (!s) return;
    if (s.claimedby !== this.me.sub && !this.isAdmin()) {
      return this.fail(id, "This system is no longer claimed by you.");
    }
    this.pendingCheckShot.delete(id);
    s.status = "online";
    this.change(s);
  }

  private removeFromPool(id: string) {
    if (!this.isAdmin() || !this.systems.has(id)) return;
    this.stopStream(id);
    this.systems.delete(id);
    this.send("system_removed", { id });
  }

  private restartProcess(id: string) {
    const s = this.systems.get(id);
    if (!this.isAdmin() || !s) return;
    if (s.status === "Offline") {
      return this.fail(id, "System is not reachable right now — it may still be reconnecting. Try again shortly.");
    }
    this.after(1200, () => this.systems.get(id) === s && this.takeOffline(s));
    this.after(7000, () => this.systems.get(id) === s && this.bringOnline(s));
  }

  private writeNote(id: string, note?: string) {
    const s = this.systems.get(id);
    if (!s) return;
    if (s.claimedby !== this.me.sub && !this.isAdmin()) {
      return this.fail(id, "You must claim this system before adding a note.");
    }
    const finalNote = (typeof note === "string" && note.trim()) || `Checked by ${this.me.fullname}`;
    this.recordCheck(s, this.me, finalNote);
    this.change(s);
    this.streams.get(id)?.station.addNote(finalNote);
    this.after(900, () => this.send("wn_done", { success: true }));
  }

  // ── Live view: a real peer connection, looped back inside the page ───────

  private async requestStream(id: string) {
    const s = this.systems.get(id);
    if (!s) return;
    if (s.claimedby !== this.me.sub && !this.isAdmin()) {
      return this.fail(id, "You must claim this system before opening live view.");
    }
    if (s.status === "Offline") {
      this.send("system_offline", { ...s });
      return this.fail(id, "System is not reachable right now — it may still be reconnecting. Try again shortly.");
    }
    this.stopStream(id, false);

    // The viewer and this fake workstation share one browser, so the viewer
    // skips STUN and TURN entirely (`local`) and connects over loopback.
    this.send("TURN_CREDENTIALS", { username: "", credential: "", forceRelay: false, local: true, systemId: id });
    s.is_live_view = "true";
    this.change(s);

    const station = new FakeWorkstation(s.patient_name, s.system_name, s.note ? [s.note] : []);
    station.badgeName = this.me.fullname;
    const media = station.start(15);
    const pc = new RTCPeerConnection();
    media.getTracks().forEach((t) => {
      // Screen content: keep full resolution and let frame rate give way,
      // otherwise the encoder starts at a blurry 320x180 and ramps up.
      (t as any).contentHint = "detail";
      const sender = pc.addTrack(t, media);
      try {
        const params = sender.getParameters();
        (params as any).degradationPreference = "maintain-resolution";
        if (!params.encodings || params.encodings.length === 0) params.encodings = [{}];
        params.encodings[0].maxBitrate = 2_500_000;
        sender.setParameters(params).catch(() => {});
      } catch (_) {}
    });
    // The workstation side opens the input channel; the viewer only binds it.
    const dc = pc.createDataChannel("input");
    dc.onmessage = (e) => {
      try {
        station.handleInput(JSON.parse(e.data));
      } catch (_) {}
    };
    pc.onicecandidate = (e) => {
      if (e.candidate) this.send("STREAM_ICE_FROM_SYSTEM", { candidate: e.candidate.toJSON(), systemId: id });
    };

    const session: LiveSession = {
      id: this.nextSessionId++,
      user_id: this.me.sub,
      username: this.me.fullname,
      system_id: id,
      systemname: s.system_name,
      patientname: s.patient_name,
      started_at: new Date().toISOString(),
      ended_at: null,
      duration_seconds: null,
    };
    this.liveSessions.push(session);

    const statsTimer = setInterval(() => {
      this.send("STREAM_STATS", {
        systemId: id,
        bitrate_kbps: 1050 + Math.round(Math.random() * 180),
        available_kbps: 2400,
        fps: 15,
        width: 1280,
        height: 720,
        rtc_rtt_ms: Math.max(4, Math.round(Number(s.ping) * 0.8)),
        quality_reason: "none",
        preset: "L4",
      });
    }, 2000);

    this.streams.set(id, { pc, station, pending: [], answered: false, statsTimer, session });

    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      if (this.streams.get(id)?.pc !== pc) return; // superseded while awaiting
      this.send("STREAM_OFFER", { offer: { type: offer.type, sdp: offer.sdp }, systemId: id });
    } catch (e) {
      console.error("[sandbox] could not create stream offer", e);
      this.stopStream(id);
    }
  }

  private async streamAnswer(id: string, answer: RTCSessionDescriptionInit) {
    const st = this.streams.get(id);
    if (!st || !answer) return;
    try {
      await st.pc.setRemoteDescription(answer);
      st.answered = true;
      for (const c of st.pending.splice(0)) await st.pc.addIceCandidate(c).catch(() => {});
    } catch (e) {
      console.error("[sandbox] stream answer rejected", e);
    }
  }

  private async streamIce(id: string, candidate: RTCIceCandidateInit) {
    const st = this.streams.get(id);
    if (!st || !candidate) return;
    if (!st.answered) st.pending.push(candidate);
    else await st.pc.addIceCandidate(candidate).catch(() => {});
  }

  private stopStream(id: string, announce = true) {
    const st = this.streams.get(id);
    if (st) {
      clearInterval(st.statsTimer);
      st.station.stop();
      try {
        st.pc.close();
      } catch (_) {}
      const started = new Date(st.session.started_at).getTime();
      st.session.ended_at = new Date().toISOString();
      st.session.duration_seconds = Math.max(1, Math.round((Date.now() - started) / 1000));
      this.streams.delete(id);
    }
    const s = this.systems.get(id);
    if (s && s.is_live_view === "true") {
      s.is_live_view = "false";
      if (announce) this.change(s);
    }
  }

  // ── Used by the HTTP mock ────────────────────────────────────────────────

  orgSystems() {
    return Array.from(this.systems.values()).map((s) => ({
      id: s.id,
      system_name: s.system_name,
      guid: this.guids.get(s.id),
      status: "active",
      version: s.version,
    }));
  }

  /** Admin adds a system; a few seconds later it "installs" and comes online. */
  createSystem(name: string) {
    const n = this.nextSystemNum++;
    const id = String(900 + n);
    const guid = this.makeGuid();
    const system_name = (name || "").trim() || `SYS ${n}`;
    this.after(8000, () => {
      const s: Sys = {
        id,
        organization_id: String(SANDBOX_ORG_ID),
        system_name,
        patient_name: "",
        status: "neuroworksClosed",
        study_active: "false",
        battery_lvl: "100",
        charge_status: "true",
        network_type: "wifi",
        ping: "44",
        claimedby: "",
        last_checked: "",
        last_seen_at: String(Date.now()),
        note: "",
        is_live_view: "false",
        remote_session: "",
        version: "3.0.5",
      };
      this.systems.set(id, s);
      this.guids.set(id, guid);
      this.send("system_online", { ...s });
    });
    return { id, system_name, guid };
  }

  deactivateSystem(id: string) {
    if (!this.systems.has(id)) return false;
    this.stopStream(id);
    this.systems.delete(id);
    this.send("system_removed", { id });
    return true;
  }

  /** Visitor-triggered events from the sandbox controls. */
  demo(action: "reminder" | "offline" | "online" | "study") {
    const mine = Array.from(this.systems.values()).filter((s) => s.claimedby === this.me.sub);
    if (action === "reminder") {
      const s = mine.find((x) => x.status === "online" && x.patient_name) || mine[0];
      if (s) this.send("check_reminder", { systemId: s.id, systemName: s.system_name, patientName: s.patient_name, reminderTime: "0h 45m" });
    } else if (action === "offline") {
      const s = mine.find((x) => x.status !== "Offline" && !BUSY.includes(x.status));
      if (s) this.takeOffline(s);
    } else if (action === "online") {
      const s = Array.from(this.systems.values()).find((x) => x.status === "Offline");
      if (s) this.bringOnline(s);
    } else if (action === "study") {
      const s = Array.from(this.systems.values()).find((x) => x.status === "neuroworksClosed");
      if (s) {
        s.patient_name = `Demo Patient ${String.fromCharCode(75 + (this.tick % 12))}`;
        s.status = "online";
        s.study_active = "true";
        s.last_checked = "";
        this.change(s);
      }
    }
  }
}

export const simulator = new Simulator();
